import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, MessageSquare, ShieldAlert, CheckCircle, Info, Sparkles, X, Megaphone } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';

export function NotificationDropdown({ user }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const dropdownRef = useRef(null);

  const { notifications, unreadCount, markAsRead, deleteNotification, clearAll, fetchNotifications } = useNotifications(user);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'announcement':
        return <Megaphone className="w-4 h-4 text-amber-600 animate-bounce" />;
      case 'message':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'concern':
      case 'concern_reply':
        return <ShieldAlert className="w-4 h-4 text-amber-500" />;
      case 'request':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      default:
        return <Info className="w-4 h-4 text-indigo-500" />;
    }
  };

  const handleNotificationClick = (item) => {
    markAsRead(item.id);
    setIsOpen(false);

    if (item.type === 'announcement') {
      navigate('/announcements');
    } else if (item.type === 'message') {
      navigate('/messages');
    } else if (item.type === 'concern' && user?.role === 'admin') {
      navigate('/admin/concerns');
    } else if (item.type === 'request' && user?.role === 'admin') {
      navigate('/admin/profile-requests');
    }
  };

  const filtered = notifications.filter(n => {
    if (activeTab === 'all') return true;
    if (activeTab === 'announcements') return n.type === 'announcement';
    if (activeTab === 'messages') return n.type === 'message';
    if (activeTab === 'concerns') return n.type === 'concern' || n.type === 'concern_reply';
    if (activeTab === 'system') return n.type === 'system' || n.type === 'request';
    return true;
  });

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        id="btn-notification-bell"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) fetchNotifications();
        }}
        className="relative p-2 text-white/90 hover:text-white hover:bg-white/10 rounded-xl transition duration-200 focus:outline-none"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white ring-2 ring-amber-900 animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 z-[110] overflow-hidden border border-gray-100 animate-slide-up">
          {/* Panel Header */}
          <div className="bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 p-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <h3 className="font-bold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-amber-500/30 text-amber-100 border border-amber-400/30 text-[11px] font-semibold px-2 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => markAsRead(null)}
                  className="text-xs text-amber-200 hover:text-white flex items-center gap-1 font-medium hover:underline transition"
                  title="Mark all as read"
                >
                  <Check className="w-3.5 h-3.5" /> Read All
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex border-b border-gray-100 bg-gray-50/70 p-1.5 gap-1 text-xs">
            {['all', 'announcements', 'messages', 'concerns'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-1.5 rounded-lg font-medium transition text-center capitalize ${
                  activeTab === tab
                    ? 'bg-white text-amber-900 font-bold shadow-sm border border-gray-200/50'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'announcements' ? '📢 Alerts' : tab}
              </button>
            ))}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 text-xs">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="font-medium text-gray-500">No notifications</p>
                <p className="text-[11px] mt-0.5">You're all caught up!</p>
              </div>
            ) : (
              filtered.map(item => (
                <div
                  key={item.id}
                  className={`p-3.5 flex items-start gap-3 transition group relative ${
                    !item.read ? 'bg-amber-50/30' : 'bg-white'
                  } hover:bg-amber-50/60`}
                >
                  {/* Clickable Area for Navigation */}
                  <div
                    onClick={() => handleNotificationClick(item)}
                    className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer"
                  >
                    <div className="p-2 rounded-xl bg-gray-100 flex-shrink-0 mt-0.5 group-hover:bg-amber-100/60 transition">
                      {getIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className={`font-semibold text-xs truncate ${!item.read ? 'text-amber-950 font-bold' : 'text-gray-800'}`}>
                          {item.title}
                        </p>
                        {!item.read && (
                          <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-gray-600 text-[11px] mt-0.5 line-clamp-2 leading-relaxed">
                        {item.message}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {item.createdAt ? new Date(item.createdAt).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                      </p>
                    </div>
                  </div>

                  {/* Real-time Individual Delete Icon Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(item.id);
                    }}
                    className="opacity-40 group-hover:opacity-100 p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition duration-150 flex-shrink-0 self-center"
                    title="Delete notification"
                    aria-label="Delete notification"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Panel Footer */}
          {notifications.length > 0 && (
            <div className="p-2 bg-gray-50 border-t border-gray-100 text-center flex justify-between items-center px-4">
              <span className="text-[11px] text-gray-500">{notifications.length} total items</span>
              <button
                onClick={clearAll}
                className="text-[11px] text-rose-600 hover:text-rose-800 font-semibold flex items-center gap-1 hover:underline transition"
              >
                <Trash2 className="w-3 h-3" /> Clear list
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
